import {use, useCallback, useRef, useState} from 'react'
import {useSafeChatStateLens} from '@github-ui/copilot-chat/CopilotChatContext'
import {useSafeChatManager} from '@github-ui/copilot-chat/CopilotChatManagerContext'
import {
  ConversationFeedbackDialog,
  type FeedbackDialogRef,
} from '@github-ui/copilot-chat/components/ConversationFeedbackDialog'
import {PersonalInstructionsDialog} from '@github-ui/copilot-chat/components/PersonalInstructionsDialog'
import {getPlanLabel} from '@github-ui/copilot-chat/utils/copilot-plan'
import {UPGRADE_PLAN_URL_NEW} from '@github-ui/copilot-chat-helpers/copilot-chat-helpers'
import {CopilotLicenseType, type CopilotChatOrg} from '@github-ui/copilot-chat-types'
import {useOptionalPersonalInstructionsEditor} from '@github-ui/copilot-shared-ui/contexts/ContributedInstructionsContext'
import {
  EntitlementContext,
  EntitlementProvider,
  useEntitlement,
} from '@github-ui/copilot-entitlement/EntitlementContext'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {generateSSOUrl} from '@github-ui/global-sso-banner-ui/generate-sso-url'
import {sendEvent} from '@github-ui/hydro-analytics'
import {SidebarTooltip} from '@github-ui/layout-primitives/SidebarTooltip'
import {useQueryClient} from '@github-ui/react-query'
import {useLocation} from '@github-ui/react-router'
import {CommentIcon, GearIcon, NoteIcon, RocketIcon, ShieldLockIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button, IconButton, Link} from '@primer/react'

import {copilotChatPayloadRouteQuery, type ChatPayload} from './copilot-chat-payload-route-query'
import styles from './AgentsSidebarUserMenu.module.css'

interface AgentsSidebarUserMenuProps {
  currentUserLogin: string
  isExpanded: boolean
  chatPayload?: ChatPayload
  isError?: boolean
}

export function AgentsSidebarUserMenu(props: AgentsSidebarUserMenuProps) {
  const {chatPayload} = props
  const parentEntitlement = use(EntitlementContext)

  // The footer only needs entitlements, not a second chat manager or conversation state.
  return (
    <EntitlementProvider
      initialLicenseType={chatPayload?.licenseType ?? parentEntitlement?.licenseType ?? CopilotLicenseType.Unlicensed}
      initialPlan={chatPayload?.plan ?? parentEntitlement?.plan ?? undefined}
      initialQuotas={chatPayload?.quotas}
      hasCEorCBAccess={chatPayload?.hasCEorCBAccess}
      initialCanUpgradePlan={chatPayload?.canUpgradePlan ?? parentEntitlement?.canUpgradePlan}
    >
      <UserMenu {...props} />
    </EntitlementProvider>
  )
}

function UserMenu({currentUserLogin, isExpanded, chatPayload, isError = false}: AgentsSidebarUserMenuProps) {
  const anchorRef = useRef<HTMLButtonElement>(null)
  const feedbackRef = useRef<FeedbackDialogRef>(null)
  const [showPersonalInstructions, setShowPersonalInstructions] = useState(false)
  const personalInstructionsEditor = useOptionalPersonalInstructionsEditor()
  const chatManager = useSafeChatManager()
  const queryClient = useQueryClient()
  const parentPersonalInstructions = useSafeChatStateLens(state => state?.personalInstructions)
  const personalInstructions = personalInstructionsEditor
    ? personalInstructionsEditor.personalInstructions
    : chatManager
      ? parentPersonalInstructions
      : chatPayload?.personalInstructions
  const [savedInstructions, setSavedInstructions] = useState<{
    source: string | null | undefined
    value: string
  }>()
  const canEditPersonalInstructions = Boolean(chatPayload || chatManager)
  const handlePersonalInstructionsChange = useCallback(
    (value: string) => {
      // Supplied route payloads stay unchanged after saving; retain the edit until their source changes.
      setSavedInstructions({source: personalInstructions, value})
      personalInstructionsEditor?.updatePersonalInstructions(value)
      chatManager?.dispatch({type: 'SET_PERSONAL_INSTRUCTIONS', personalInstructions: value})
      queryClient.setQueryData<ChatPayload>(['copilot-agents', 'chat-payload'], previous => {
        const payload = previous ?? chatPayload
        return payload ? {...payload, personalInstructions: value} : undefined
      })
      // Legacy conversations and Space details keep their own route payloads across soft navigation.
      queryClient.setQueriesData<ChatPayload>(
        {
          predicate: ({queryKey: [appName, , , queryName]}) =>
            appName === 'copilot-agents' && queryName === copilotChatPayloadRouteQuery.queryName,
        },
        previous => (previous ? {...previous, personalInstructions: value} : undefined),
      )
    },
    [personalInstructions, personalInstructionsEditor, chatManager, queryClient, chatPayload],
  )
  const entitlement = useEntitlement()
  const parentPlan = useSafeChatStateLens(state => state?.plan)
  const parentOptedInToFeedback = useSafeChatStateLens(state => state?.optedInToUserFeedback)
  const parentSsoOrganizations = useSafeChatStateLens(state => state?.ssoOrganizations)
  const ssoOrganizations = parentSsoOrganizations ?? chatPayload?.ssoOrganizations
  const plan = entitlement.plan ?? chatPayload?.plan ?? parentPlan
  const planLabel = plan ? `Copilot ${getPlanLabel(plan)}` : undefined
  const accountDescription = planLabel ?? (isError ? 'Account details unavailable' : undefined)
  const {canUpgradePlan} = entitlement
  const optedInToFeedback = chatPayload?.optedInToUserFeedback ?? parentOptedInToFeedback
  const avatar = <GitHubAvatar src={`/${currentUserLogin}.png`} size={24} alt="" />
  const trackUpgrade = () =>
    sendEvent('dotcom_chat.activate', {
      target: isExpanded ? 'UPGRADE_SIDEBAR_LINK' : 'UPGRADE_USER_MENU_LINK',
      mode: 'immersive',
    })

  return (
    <div className={styles.Account} data-expanded={isExpanded}>
      <div className={styles.AccountMenu}>
        <ActionMenu anchorRef={anchorRef}>
          <ActionMenu.Anchor>
            <Button
              variant="invisible"
              alignContent="start"
              block
              labelWrap
              className={styles.AccountButton}
              aria-label={`${currentUserLogin}${accountDescription ? `, ${accountDescription}` : ''}, user menu`}
              leadingVisual={() => avatar}
            >
              {isExpanded && (
                <span className={styles.AccountDetails}>
                  <span>{currentUserLogin}</span>
                  {isError && accountDescription && <span className={styles.Plan}>{accountDescription}</span>}
                </span>
              )}
            </Button>
          </ActionMenu.Anchor>
          <ActionMenu.Overlay side="outside-top" align="start" width="small">
            <div className={styles.MenuAccount}>
              {avatar}
              <div className={styles.AccountDetails}>
                <span>{currentUserLogin}</span>
                {accountDescription && <span className={styles.Plan}>{accountDescription}</span>}
              </div>
            </div>
            <ActionList>
              <ActionList.Item
                disabled={!canEditPersonalInstructions}
                onSelect={() => {
                  setShowPersonalInstructions(true)
                  sendEvent('dotcom_chat.activate', {target: 'USER_MENU_PERSONAL_INSTRUCTIONS', mode: 'immersive'})
                }}
              >
                <ActionList.LeadingVisual>
                  <NoteIcon />
                </ActionList.LeadingVisual>
                Personal instructions
                {!canEditPersonalInstructions && (
                  <ActionList.Description variant="block">
                    {isError ? 'Account details unavailable' : 'Loading account details'}
                  </ActionList.Description>
                )}
              </ActionList.Item>
              {!isExpanded && canUpgradePlan && (
                <ActionList.LinkItem href={UPGRADE_PLAN_URL_NEW} onClick={trackUpgrade}>
                  <ActionList.LeadingVisual>
                    <RocketIcon />
                  </ActionList.LeadingVisual>
                  Upgrade plan
                </ActionList.LinkItem>
              )}
              {optedInToFeedback && (
                <ActionList.Item
                  onSelect={() => {
                    feedbackRef.current?.openDialog()
                    sendEvent('dotcom_chat.activate', {target: 'USER_MENU_GIVE_FEEDBACK', mode: 'immersive'})
                  }}
                >
                  <ActionList.LeadingVisual>
                    <CommentIcon />
                  </ActionList.LeadingVisual>
                  Give feedback
                </ActionList.Item>
              )}
              <ActionList.LinkItem
                href="/settings/copilot"
                onClick={() => sendEvent('dotcom_chat.activate', {target: 'USER_MENU_SETTINGS', mode: 'immersive'})}
              >
                <ActionList.LeadingVisual>
                  <GearIcon />
                </ActionList.LeadingVisual>
                Settings
              </ActionList.LinkItem>
            </ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
      </div>
      <SingleSignOn organizations={ssoOrganizations} />
      {isExpanded && canUpgradePlan && (
        <Link className={styles.UpgradeLink} href={UPGRADE_PLAN_URL_NEW} onClick={trackUpgrade}>
          Upgrade plan
        </Link>
      )}
      <ConversationFeedbackDialog ref={feedbackRef} mode="immersive" returnFocusRef={anchorRef} />
      {showPersonalInstructions && (
        <PersonalInstructionsDialog
          onDismiss={() => setShowPersonalInstructions(false)}
          returnFocusRef={anchorRef}
          personalInstructions={
            savedInstructions && savedInstructions.source === personalInstructions
              ? savedInstructions.value
              : personalInstructions
          }
          onPersonalInstructionsChange={handlePersonalInstructionsChange}
        />
      )}
    </div>
  )
}

function SingleSignOn({organizations}: {organizations: CopilotChatOrg[] | undefined}) {
  const location = useLocation()
  const firstOrganization = organizations?.[0]
  if (!firstOrganization) return null

  if (organizations.length === 1) {
    return (
      <SidebarTooltip text="Single sign-on">
        {/* eslint-disable-next-line primer-react/a11y-remove-disable-tooltip -- SidebarTooltip provides the visible hover and focus label */}
        <IconButton
          as="a"
          href={generateSSOUrl(firstOrganization.login, location)}
          variant="invisible"
          icon={ShieldLockIcon}
          aria-label="Single sign-on"
          unsafeDisableTooltip
        />
      </SidebarTooltip>
    )
  }

  return (
    <SidebarTooltip text="Single sign-on">
      <div>
        <ActionMenu>
          <ActionMenu.Anchor>
            {/* eslint-disable-next-line primer-react/a11y-remove-disable-tooltip -- SidebarTooltip provides the visible hover and focus label */}
            <IconButton variant="invisible" icon={ShieldLockIcon} aria-label="Single sign-on" unsafeDisableTooltip />
          </ActionMenu.Anchor>
          <ActionMenu.Overlay side="outside-top" align="start">
            <ActionList>
              {organizations.map(org => (
                <ActionList.LinkItem key={org.login} href={generateSSOUrl(org.login, location)}>
                  {org.login}
                </ActionList.LinkItem>
              ))}
            </ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
      </div>
    </SidebarTooltip>
  )
}
